// built-from: /current-state-mapping-initiation v1 impl:current-state-mapping-initiation role:implementation
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export type CurrentStateMappingSetup = {
  question: string;
  streamName: string;
  startBoundary: string;
  endBoundary: string;
  participants: string;
  reviewer: string;
};

export type CurrentStateMappingInitiationStatus =
  | 'idle'
  | 'starting'
  | 'resuming'
  | 'recovery'
  | 'failed';

type Props = {
  status: CurrentStateMappingInitiationStatus;
  initialValues: CurrentStateMappingSetup;
  error?: string | null;
  onCancel: () => void;
  onSubmit: (values: CurrentStateMappingSetup) => void;
};

export const emptyCurrentStateMappingSetup: CurrentStateMappingSetup = {
  question: '',
  streamName: '',
  startBoundary: '',
  endBoundary: '',
  participants: '',
  reviewer: '',
};

export function CurrentStateMappingInitiation({
  status,
  initialValues,
  error,
  onCancel,
  onSubmit,
}: Props): JSX.Element {
  const [values, setValues] = useState(initialValues);
  useEffect(() => setValues(initialValues), [initialValues]);

  const busy = status === 'starting' || status === 'resuming';
  const recovering = status === 'recovery';
  const failed = status === 'failed';
  const readOnly = busy || recovering;
  const complete = Object.values(values).every((value) => value.trim().length > 0);
  const update = (field: keyof CurrentStateMappingSetup, value: string): void => {
    setValues((current) => ({ ...current, [field]: value }));
  };

  if (recovering) {
    return (
      <div className="journey-page mapping-start-page">
        <nav className="journey-breadcrumb" aria-label="Breadcrumb">
          <Link to="/engagement/discovery">Discovery</Link><span aria-hidden="true">/</span><span>Finish setup</span>
        </nav>
        <header className="journey-page__header" tabIndex={-1}>
          <span className="journey-eyebrow">Interrupted setup</span>
          <h1>Mapping setup needs finishing</h1>
          <p>Your details are safe, and the activity is not visible yet. Finish setup to continue; this won’t create a duplicate.</p>
        </header>
        <section className="journey-panel mapping-recovery-summary">
          <dl>
            <div><dt>Question</dt><dd>{values.question}</dd></div>
            <div><dt>Value stream</dt><dd>{values.streamName}</dd></div>
            <div><dt>Boundary</dt><dd>{values.startBoundary} → {values.endBoundary}</dd></div>
            <div><dt>People</dt><dd>{values.participants}; reviewed by {values.reviewer}</dd></div>
          </dl>
          <div className="mapping-form-actions">
            <button className="journey-button journey-button--secondary" type="button" onClick={onCancel}>Back to Discovery</button>
            <button className="journey-button journey-button--primary" type="button" onClick={() => onSubmit(values)}>Finish starting mapping</button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="journey-page mapping-start-page">
      <nav className="journey-breadcrumb" aria-label="Breadcrumb">
        <button type="button" disabled={busy} onClick={onCancel}>Discovery</button><span aria-hidden="true">/</span><span>Start current-state mapping</span>
      </nav>
      <header className="journey-page__header">
        <span className="journey-eyebrow">New Discovery activity</span>
        <h1>Start current-state mapping</h1>
        <p>Set the question and boundaries for this activity. Participants can build and correct the map together in the workspace.</p>
      </header>

      {failed ? (
        <div className="mapping-start-alert" role="alert">
          <strong>Mapping could not be started</strong>
          <span>{error || 'Your entered details are still here. Check your connection and try again.'}</span>
        </div>
      ) : null}

      <form className="mapping-start-layout" onSubmit={(event) => { event.preventDefault(); onSubmit(values); }}>
        <main className="mapping-start-fields">
          <section className="journey-panel mapping-form-section">
            <span className="mapping-section-number" aria-hidden="true">1</span>
            <div className="mapping-section-content">
              <label htmlFor="mapping-question">What do we need to understand?</label>
              <p>Use one question that this activity should help the engagement answer.</p>
              <textarea id="mapping-question" rows={3} value={values.question} readOnly={readOnly} onChange={(event) => update('question', event.target.value)} />
            </div>
          </section>

          <section className="journey-panel mapping-form-section">
            <span className="mapping-section-number" aria-hidden="true">2</span>
            <div className="mapping-section-content mapping-grid">
              <div className="mapping-field mapping-field--wide">
                <label htmlFor="mapping-stream">Value stream</label>
                <input id="mapping-stream" value={values.streamName} readOnly={readOnly} onChange={(event) => update('streamName', event.target.value)} />
              </div>
              <div className="mapping-field">
                <label htmlFor="mapping-start">Starts when</label>
                <input id="mapping-start" value={values.startBoundary} readOnly={readOnly} onChange={(event) => update('startBoundary', event.target.value)} />
              </div>
              <div className="mapping-field">
                <label htmlFor="mapping-end">Ends when</label>
                <input id="mapping-end" value={values.endBoundary} readOnly={readOnly} onChange={(event) => update('endBoundary', event.target.value)} />
              </div>
            </div>
          </section>

          <section className="journey-panel mapping-form-section">
            <span className="mapping-section-number" aria-hidden="true">3</span>
            <div className="mapping-section-content mapping-grid">
              <div className="mapping-field">
                <label htmlFor="mapping-participants">Participants</label>
                <input id="mapping-participants" value={values.participants} readOnly={readOnly} onChange={(event) => update('participants', event.target.value)} />
                <small>Separate multiple names with commas.</small>
              </div>
              <div className="mapping-field">
                <label htmlFor="mapping-reviewer">Reviewer</label>
                <input id="mapping-reviewer" value={values.reviewer} readOnly={readOnly} onChange={(event) => update('reviewer', event.target.value)} />
                <small>Defaults to the signed-in consultant when available.</small>
              </div>
            </div>
          </section>
        </main>

        <aside className="journey-panel mapping-start-summary">
          <span className="journey-eyebrow">Fixed for this activity</span>
          <h2>What ArcPilot will prepare</h2>
          <dl>
            <div><dt>Method</dt><dd>Voice-led value-stream mapping</dd></div>
            <div><dt>Expected output</dt><dd>Reviewed current-state value-stream model</dd></div>
            <div><dt>Activity owner</dt><dd>{values.reviewer || 'Reviewer required'}</dd></div>
          </dl>
          {busy ? (
            <div className="mapping-start-progress" aria-live="polite">
              <span className="mapping-start-spinner" aria-hidden="true" />
              <div>
                <strong>{status === 'resuming' ? 'Finishing mapping setup…' : 'Starting mapping…'}</strong>
                <p>{status === 'resuming'
                  ? 'ArcPilot found an unfinished setup and is safely completing it. No duplicate activity will be created.'
                  : 'ArcPilot is creating the activity and preparing its workspace. You’ll continue automatically when it’s ready.'}</p>
              </div>
            </div>
          ) : null}
          <div className="mapping-form-actions">
            <button className="journey-button journey-button--secondary" type="button" disabled={busy} onClick={onCancel}>{failed ? 'Edit details' : 'Cancel'}</button>
            <button className="journey-button journey-button--primary" type="submit" disabled={!complete || busy}>
              {busy ? 'Starting mapping…' : failed ? 'Retry' : 'Create and open workspace'}
            </button>
          </div>
        </aside>
      </form>
    </div>
  );
}
